/**
 * Free, deterministic starter logos — no AI cost. From a company name + a board's primary
 * color + heading font we generate an SVG monogram badge (favicon/icon) and a horizontal
 * wordmark lockup (primary logo). Agents get a clean, on-brand placeholder they can replace
 * with a real/AI logo later. SVG is an allowed brand-logo upload type on the gateway.
 */

/** Up to 3 uppercase initials from a company name (skips filler words like "the", "of", "and").
 *  "Listing World Realty" → "LWR", "Coastal Breeze" → "CB", 4+ words → first+last. */
export function brandInitials(name: string): string {
  const stop = new Set(['the', 'of', 'and', 'a', 'an', 'for', 'at', 'in', 'on', 'by', '&']);
  const words = (name || '')
    .replace(/[^a-zA-Z0-9\s&]/g, ' ')
    .split(/\s+/)
    .filter((w) => w && !stop.has(w.toLowerCase()));
  if (words.length === 0) return (name.trim()[0] || 'A').toUpperCase();
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  if (words.length <= 3) return words.map((w) => w[0]).join('').toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/** Badge font-size that keeps 1–3 initials comfortably inside a square badge of `box` px. */
function badgeFontSize(initials: string, box: number): number {
  const ratio = initials.length >= 3 ? 0.42 : initials.length === 2 ? 0.5 : 0.6;
  return Math.round(box * ratio);
}

/** Choose black or white text for legible contrast on a hex background. */
export function contrastInk(hex: string): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex || '');
  if (!m) return '#ffffff';
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  // Relative luminance (sRGB) — > ~0.6 reads as "light", use dark ink.
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? '#10032A' : '#ffffff';
}

function esc(s: string): string {
  return (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fontStack(family?: string): string {
  const fam = (family || '').trim();
  const safe = fam ? `'${fam.replace(/'/g, '')}', ` : '';
  return `${safe}'Georgia', 'Segoe UI', system-ui, -apple-system, sans-serif`;
}

export interface MonogramOpts {
  companyName: string;
  primary: string;       // badge fill
  ink?: string;          // wordmark text color (defaults to a near-black)
  headingFont?: string;
}

/** Square badge with the company initials — the favicon / icon variant (works down to 16px). */
export function faviconSvg({ companyName, primary, headingFont }: MonogramOpts): string {
  const initials = brandInitials(companyName);
  const ink = contrastInk(primary);
  const fs = badgeFontSize(initials, 64);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" role="img" aria-label="${esc(companyName)} icon">
  <rect width="64" height="64" rx="14" fill="${primary}"/>
  <text x="32" y="33" fill="${ink}" font-family="${fontStack(headingFont)}" font-size="${fs}" font-weight="700" text-anchor="middle" dominant-baseline="central" letter-spacing="0.5">${esc(initials)}</text>
</svg>`;
}

/** Horizontal lockup: initials badge + company name — the primary header logo. */
export function wordmarkSvg({ companyName, primary, ink, headingFont }: MonogramOpts): string {
  const initials = brandInitials(companyName);
  const badgeInk = contrastInk(primary);
  const textInk = ink || '#10032A';
  const name = companyName.trim() || 'Your Company';
  const badgeFs = badgeFontSize(initials, 56);
  // Estimate wordmark width so the viewBox hugs the content (avoids clipping / huge gaps).
  const nameFs = 30;
  const nameW = Math.ceil(name.length * nameFs * 0.6);
  const W = 20 + 56 + 16 + nameW + 20; // padL + badge + gap + name + padR
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="88" viewBox="0 0 ${W} 88" role="img" aria-label="${esc(name)}">
  <rect x="20" y="16" width="56" height="56" rx="13" fill="${primary}"/>
  <text x="48" y="45" fill="${badgeInk}" font-family="${fontStack(headingFont)}" font-size="${badgeFs}" font-weight="700" text-anchor="middle" dominant-baseline="central" letter-spacing="0.5">${esc(initials)}</text>
  <text x="92" y="45" fill="${textInk}" font-family="${fontStack(headingFont)}" font-size="${nameFs}" font-weight="600" dominant-baseline="central" letter-spacing="0.2">${esc(name)}</text>
</svg>`;
}

/** Wrap an SVG string as a File for multipart upload (uploadLogo). */
export function svgToFile(svg: string, filename: string): File {
  return new File([svg], filename, { type: 'image/svg+xml' });
}

/** Inline data URI for previewing an SVG in an <img> without uploading. */
export function svgDataUri(svg: string): string {
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/** Decode a base64 image (e.g. an AI-generated logo) into a File for multipart upload. */
export function base64ToFile(base64: string, mimeType: string, filename: string): File {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], filename, { type: mimeType });
}
